import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { GenericFormService } from './service/generic-form.service';
import { of, Subject, takeUntil, distinctUntilChanged } from 'rxjs';
import { FieldModel } from './model/field.model';
import { VisibilityContext } from './service/visibility-context';
import { FormModelService } from './service/form-model.service';
import { ObjectUtils, ReactiveFormUtils } from '@cv-invision/core';
import { set } from 'lodash-es';

@Component({
	selector: 'cv-form-field',
	templateUrl: './form-field.component.html',
	styleUrls: ['./form-field.component.scss']
})
export class FormFieldComponent implements OnInit, OnDestroy {

	@ViewChild('reviewField', { read: ElementRef, static: false }) reviewField: ElementRef;
	@Input() options: any;
	@Input() dynamicParams?: any;
	/**
	 * cv-invision\libs\cv.invision-views.ux\src\lib\generic-crud-table\components\create-update-form\create-update-form.component.html
	   cv-invision\libs\cv.global-configuration.ux\src\lib\generic-integrations\components\common\create-update-form\create-update-form.component.html
	 * @deprecated
	 */
	@Input() model: any;

	paths: any = {};
	formControl: FormControl;
	public field: FieldModel;
	value: any;
	visible: boolean;

	private atComponentDestroy = new Subject();

	get form(): FormGroup {
		return this.formService.form;
	}

	get dataModel(): any {
		return this.formService.dataModel;
	}

	get formFieldList(): any {
		return this.formService.formFieldList;
	}

	constructor(
		private formService: GenericFormService,
		private formModelService: FormModelService
	) {
		this.visible = true;
	}

	async ngOnInit(): Promise<void> {
		this.paths = ReactiveFormUtils.getPaths(this.options.path);
		await this.setFormControl()
		await this.setField();
		await this.setValue();
		await this.setValidations();
		await this.isVisible();

		this.formService.form.valueChanges
			.pipe(
				distinctUntilChanged(),
				takeUntil(this.atComponentDestroy)
			).subscribe(async data => {
				await this.setValue();
				await this.isVisible();
			});
	}

	/**
	 * Set the field different paths,
	 */
	async setField(): Promise<any> {
		if ('childKey' in this.options) {
			const formBuilderConfig = this.formModelService.getValue(this.formFieldList, (this.paths.schema));
			this.field = formBuilderConfig.child[this.options.index][this.options.childKey];
		} else {
			this.field = this.formModelService.getValue(this.formFieldList, this.paths.schema);

			if (this.formService.schema) {
				this.field = this.formService.getMetadata(this.paths.schema.split('.'));
			}
		}

		if ('label' in this.options) {
			const newLabels = {
				UILabel: this.options.label,
				DisplayText: this.options.label
			};
			Object.assign(this.field, newLabels);
		}
		if (this.dynamicParams) {
			const clonedField = ObjectUtils.recursiveDeepCopy(this.field);
			Object.keys(this.dynamicParams).forEach((key) => {
				set(clonedField, key, this.dynamicParams[key])
			});
			this.field = clonedField;
		}
		return of(void 0)
	}

	/**
	 * Set the value of the field
	 */
	async setValue(): Promise<any> {
		this.value = this.field.defaultValue || null;
		this.value = this.formControl?.value;
		return of(void 0)
	}

	/**
	 * initialize formcontrol from the given path
	 */
	async setFormControl(): Promise<any> {
		// let formData: any = this.formService.form;
		// if (this.formService.dataModel?.constructor === Array) {
		// 	const index = this.formService.dataModel.findIndex(element => element.key === this.options.key);
		// 	formData = formData.at(index) as FormControl;
		// } else {
		// 	formData = this.formService.form?.get(this.options.key) as FormControl;

		// 	if (this.options.path) {
		// 		formData = this.formService.form?.get(this.options.path) as FormControl;
		// 	}

		// 	if ('childKey' in this.options) {
		// 		formData = this.formService.form.get(this.options.path || this.options.key) as FormArray;
		// 		formData = formData.at(this.options.index)?.get(this.options.childKey);
		// 	}
		// }
		// console.log(formData)
		let formData: any = ReactiveFormUtils.getFormControl(this.formService.form, this.paths.path) as FormControl;

		if ('childKey' in this.options) {
			formData = this.formService.form.get(this.options.path) as FormArray;
			formData = formData.at(this.options.index)?.get(this.options.childKey);
		}
		this.formControl = formData;
		return of(void 0)
	}

	/**
	 *
	 */
	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}

	async setValidations(): Promise<void> {
		this.formControl?.updateValueAndValidity({onlySelf: true, emitEvent: false});
		this.formService.form.updateValueAndValidity();
	}

	async isVisible(): Promise<void> {
		let visible = true;
		if (this.field.Visible !== undefined) {
			let evaluation;
			if (typeof this.field.Visible === 'string') {
				evaluation = eval(`(${this.field.Visible})`)({}, new VisibilityContext(this.formControl, this.formModelService));
			} else {
				evaluation = this.field.Visible(this.formControl, new VisibilityContext(this.formControl, this.formModelService));
			}

			visible = evaluation as boolean;

			if (!visible) {
				if (this.formControl instanceof FormArray) {
					this.formControl.clear();
				} else {
					this.formControl.setValue(this.field.defaultValue, {onlySelf: true});
				}

			}
		}

		if ('hidden' in this.field && this.field.hidden) {
			visible = false;
		}

		this.visible = visible;
		this.isDisabled();
	}

	isDisabled(): void {
		let disabled = true;
		this.formControl?.enable({onlySelf: true, emitEvent: false});

		if (this.field.Disabled !== undefined) {
			let evaluation;
			if (typeof this.field.Disabled === 'string') {
				evaluation = eval(`(${this.field.Disabled})`)({}, new VisibilityContext(this.formControl, this.formModelService));
			} else {
				evaluation = this.field.Disabled({}, new VisibilityContext(this.formControl, this.formModelService));
			}

			disabled = evaluation as boolean;

			if (disabled) {
				this.formControl?.disable({onlySelf: true, emitEvent: false});
			}
		}

		if ('disabled' in this.field && this.field.disabled) {
			this.formControl?.disable({onlySelf: true, emitEvent: false});
		}
	}

	/**
	 * @deprecated
	 */
	isRequired(): void {
		if (this.field.isRequired !== undefined) {
			let evaluation;
			if (typeof this.field.isRequired === 'string') {
				evaluation = eval(`(${this.field.isRequired})`)({}, new VisibilityContext(this.formControl, this.formModelService));
			} else {
				evaluation = this.field.isRequired({}, new VisibilityContext(this.formControl, this.formModelService));
			}
			if (evaluation) {
				this.formControl?.setValidators([Validators.required])
			} else {
				this.formControl?.setValidators([])
			}
		}

		this.formControl?.updateValueAndValidity({onlySelf: true, emitEvent: false});
		//this.formService.form.updateValueAndValidity({onlySelf: true});


		/*if(this.formControl.parent){
			this.formControl.parent.updateValueAndValidity({ onlySelf: true, emitEvent: false});
		}*/
	}
}
